import { type ActionEvent, Controller } from "@hotwired/stimulus"

import SnackBarController from "./snackbar_controller"

import * as Ga4 from "@/helpers/ga4_helper"
import { localStorage } from "@/helpers/storage"

interface WishlistItem {
  id: string
  name: string
  [key: string]: unknown
}

const GA4_CONTENT_TYPE = "wishlist"
const STORAGE_KEY = "wishlist"
const WISHLIST_LINK = "<a href='/wishlist'>wishlist</a>"

export default class WishlistController extends Controller {
  static classes = ["active", "inactive"]
  declare readonly activeClasses: string[]
  declare readonly inactiveClasses: string[]

  static outlets = ["snackbar"]
  declare readonly snackbarOutlet: SnackBarController

  static targets = ["itemIcon"]
  declare readonly itemIconTargets: HTMLElement[]

  itemIconTargetConnected(icon: HTMLElement): void {
    if (this.hasItem(icon.dataset.id!)) {
      this.activateIcon(icon)
    } else {
      this.deactivateIcon(icon)
    }
  }

  toggleItem({ currentTarget, params: { item, removeMessage } }: ActionEvent): void {
    const icon = this.itemIconTargets.find((icon) => (currentTarget as HTMLElement).contains(icon))!

    if (this.hasItem(item.id)) {
      this.removeItem(item, icon, removeMessage)
    } else {
      this.setItem(item, icon)
    }
  }

  get wishlist(): WishlistItem[] {
    try {
      const list = localStorage?.getItem(STORAGE_KEY)
      return list ? JSON.parse(list) : []
    } catch {
      return []
    }
  }

  private activateIcon(icon: HTMLElement): void {
    icon.classList.remove(...this.inactiveClasses)
    icon.classList.add(...this.activeClasses)
  }

  private deactivateIcon(icon: HTMLElement): void {
    icon.classList.remove(...this.activeClasses)
    icon.classList.add(...this.inactiveClasses)
  }

  private hasItem(id: string): boolean {
    return this.wishlist.some((item) => item.id === id)
  }

  private removeItem(item: WishlistItem, icon: HTMLElement, message?: string): void {
    const remainingList = this.wishlist.filter((i) => i.id !== item.id)

    if (remainingList.length > 0) {
      localStorage?.setItemAtUserRequest(STORAGE_KEY, JSON.stringify(remainingList))
    } else {
      localStorage?.removeItem(STORAGE_KEY)
    }

    this.deactivateIcon(icon)
    this.showSnackbar(message || `Removed from your temporary ${WISHLIST_LINK}`)
    Ga4.clearContentEvent(GA4_CONTENT_TYPE, item.name)
  }

  private setItem(item: WishlistItem, icon: HTMLElement): void {
    const newList = [...this.wishlist, item]

    localStorage?.setItemAtUserRequest(STORAGE_KEY, JSON.stringify(newList))
    this.activateIcon(icon)
    this.showSnackbar(`Saved to your temporary ${WISHLIST_LINK}`)
    Ga4.selectContentEvent(GA4_CONTENT_TYPE, item.name)
  }

  private showSnackbar(message: string): void {
    // Don't show snackbar if `localStorage` not available
    if (localStorage) {
      void this.snackbarOutlet.show(message)
    }
  }
}
